import React, { useEffect, useState, useRef, lazy, memo, Component } from 'react';
import { Shield, Check, ArrowRight, BookOpen, Gift, Lock, Heart, Trophy, Star, Zap } from 'lucide-react';
import { PageType } from '../App';
interface SalesPageProps {
  navigate: (page: PageType) => void;
}
// ── Shared IntersectionObserver (single instance for ALL FadeIn elements) ──
const observerCallbacks = new WeakMap<Element, () => void>();
let sharedObserver: IntersectionObserver | null = null;
function getSharedObserver() {
  if (sharedObserver) return sharedObserver;
  sharedObserver = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const cb = observerCallbacks.get(entry.target);
        if (cb) {
          cb();
          observerCallbacks.delete(entry.target);
        }
        sharedObserver!.unobserve(entry.target);
      }
    });
  }, {
    rootMargin: '-40px'
  });
  return sharedObserver;
}
// ── FadeIn — lightweight, uses shared observer, composited-only animation ──
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = getSharedObserver();
    observerCallbacks.set(el, () => setVisible(true));
    obs.observe(el);
    return () => {
      obs.unobserve(el);
      observerCallbacks.delete(el);
    };
  }, []);
  return <div ref={ref} className={className} style={{
    opacity: visible ? 1 : 0,
    transform: visible ? 'none' : 'translateY(24px)',
    transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`
  }} data-id="element-4">
      {children}
    </div>;
};
// ── Static data (hoisted outside component to avoid re-creation per render) ──
const TOMOS = [{
  num: '1',
  title: 'Fundamentos Operativos y Conceptuales',
  desc: 'Cómo funciona ARCA por dentro. Qué busca. Qué detecta. Cómo posicionarte un paso adelante.'
}, {
  num: '2',
  title: 'Estrategias Fiscales Avanzadas y Litigio',
  desc: 'Cómo documentar, responder y proteger a tu cliente ante cualquier situación compleja.'
}, {
  num: '3',
  title: 'Fiscalización y Defensoría',
  desc: 'Qué hacer cuando ARCA inicia un proceso. Pasos exactos. En orden. Sin improvisar.'
}, {
  num: '4',
  title: 'Estrategias de Defensa',
  desc: 'El arsenal completo para los escenarios más frecuentes de tu estudio contable.'
}, {
  num: '5',
  title: 'Implementación y Cierre Operativo',
  desc: 'Cómo instalar el sistema en tu rutina semanal en menos de 20 minutos.'
}];
const COMPARISON_ROWS = [{
  bad: 'Presentás y rezás',
  good: 'Verificás y presentás'
}, {
  bad: 'ARCA encuentra los errores',
  good: 'Vos los encontrás primero'
}, {
  bad: 'Cada cierre genera ansiedad',
  good: 'Cada cierre es un proceso'
}, {
  bad: 'Respondés a problemas',
  good: 'Prevenís problemas'
}, {
  bad: 'Trabajás con memoria',
  good: 'Trabajás con sistema'
}];
const TESTIMONIALS = [{
  stars: 5,
  title: '"Lo compré sin muchas expectativas y me sorprendió"',
  quote: 'Soy contador hace 9 años y pensé que era otro producto genérico. Pero el checklist de 27 puntos solo ya justificó lo que pagué. La primera semana lo usé antes de tres presentaciones y en una encontré un error que no había visto. Eso no tiene precio.',
  name: 'Martín G.',
  role: 'Contador Público',
  location: 'Rosario, Santa Fe',
  img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face&fm=webp&q=75'
}, {
  stars: 5,
  title: '"El tema del DFE me pegó fuerte"',
  quote: 'Me llegó una notificación de ARCA que tenía 12 días sin ver. Por suerte llegué a tiempo, pero fue un llamado de atención. Desde que uso el protocolo de los 3 minutos los lunes, eso no me volvió a pasar. Simple pero muy efectivo.',
  name: 'Claudia R.',
  role: 'Contadora Pública Independiente',
  location: 'CABA',
  img: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face&fm=webp&q=75'
}, {
  stars: 5,
  title: '"Muy completo para el precio que tiene"',
  quote: 'Honestamente esperaba algo más básico. Los 5 tomos están bien estructurados y se nota que fue pensado para la realidad argentina, no para cualquier mercado. El tomo de fiscalización y defensoría lo leí dos veces.',
  name: 'Diego F.',
  role: 'Estudio Contable',
  location: 'Córdoba Capital',
  img: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face&fm=webp&q=75'
}, {
  stars: 5,
  title: '"Me hizo dar cuenta de cuánto estaba regalando"',
  quote: 'El bono del mapa de servicios me abrió los ojos. Tenía clientes a los que les hacía gestiones adicionales hace meses sin cobrarlas. Lo reordené todo en una semana. Ya recuperé lo que pagué con el primer cliente al que le facturé correctamente.',
  name: 'Valeria M.',
  role: 'Contadora',
  location: 'Mar del Plata',
  img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face&fm=webp&q=75'
}, {
  stars: 4,
  title: '"El tomo de defensa es muy práctico"',
  quote: 'Tuve una situación con un cliente y un requerimiento de ARCA justo después de comprar el pack. El tomo 4 me ayudó a ordenar la respuesta. No digo que fue por el libro solo, pero tener el protocolo claro me dio más seguridad para manejarlo.',
  name: 'Fernando T.',
  role: 'Contador Público',
  location: 'Tucumán',
  img: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=80&h=80&fit=crop&crop=face&fm=webp&q=75'
}];
const ESTUDIO_FEATURES = [{
  title: 'Sistema de honorarios',
  desc: 'Cuánto cobrar por cada servicio, sin subestimarte'
}, {
  title: 'Mapa de rentabilidad',
  desc: 'Cuáles clientes te dan dinero y cuáles te lo sacan'
}, {
  title: 'Protocolo de cobro',
  desc: 'Cobrar a tiempo, sin perseguir a nadie'
}, {
  title: 'Servicios adicionales',
  desc: 'Qué ofrecer y cómo presentarlo para que el cliente diga que sí'
}, {
  title: 'Gestión del estudio',
  desc: 'Trabajar menos horas con más resultado'
}];
const IA_FEATURES = ['Respuestas automáticas a clientes en segundos', 'Correos profesionales listos para enviar', 'Explicación de impuestos sin perder tiempo', 'Análisis contable más rápido', 'Organización del estudio y vencimientos'];
const PACK_ROWS = [{
  label: '5 Tomos Blindaje',
  base: true,
  pro: true
}, {
  label: '3 Bonos',
  base: true,
  pro: true
}, {
  label: 'Estudio 10X',
  base: false,
  pro: true
}, {
  label: 'Honorarios',
  base: false,
  pro: true
}, {
  label: 'Gestión y Rentabilidad',
  base: false,
  pro: true
}, {
  label: 'Sistema con IA',
  base: false,
  pro: true
}];
const PROBLEM_ITEMS = ['El requerimiento que llega sin aviso', 'La multa que no esperabas', 'El cliente que llama furioso', 'Las horas perdidas resolviendo lo que podrías haber evitado', 'Tu reputación profesional en riesgo'];
// ── Star SVG (reusable, avoids inline SVG duplication) ──
const StarIcon = ({
  filled
}: {
  filled: boolean;
}) => <svg className={`w-4 h-4 ${filled ? 'fill-gold-500 text-gold-500' : 'fill-navy-600 text-navy-600'}`} viewBox="0 0 20 20" aria-hidden="true" data-id="element-5">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-6" />
  </svg>;
// ── Main Component ──
export function SalesPage({
  navigate
}: SalesPageProps) {
  const [selectedOffer, setSelectedOffer] = useState<'base' | 'pro'>('pro');
  const ctaUrl = selectedOffer === 'base' ? 'https://fegion.shop/cart/46187419336843:1' : 'https://fegion.shop/cart/46187434344587:1';
  return <main className="min-h-screen bg-navy-900 font-sans text-offwhite" data-id="element-7">
      {/* ── HERO ── */}
      <section className="pt-12 pb-16 px-4 md:pt-20 md:pb-24 border-b border-gold-500/20 md:min-h-screen flex flex-col justify-center" data-id="element-8">
        <div className="max-w-5xl mx-auto text-center" data-id="element-9">
          <div data-id="element-10">
            <h1 className="font-bold font-serif mb-4 text-4xl md:text-6xl lg:text-7xl" style={{
            color: '#C9A84C'
          }} data-id="element-11">
              El Blindaje Operativo
            </h1>
            <p className="md:text-3xl lg:text-4xl font-bold font-serif text-white mb-8 text-xl" data-id="element-12">
              Protocolo preventivo para reducir errores sistémicos antes de
              presentar.
            </p>

            <div className="max-w-4xl mx-auto rounded-2xl overflow-hidden border border-gold-500/20 mb-10" data-id="element-13">
              <img src="https://cdn.magicpatterns.com/uploads/aYz2VWHF8Ao3Seb4BsFXiM/EL_BLINDAJE_OPERATIVO_ARCA(Ex-AFIP)_Pack_5_Tomos_2B_Bonos_-_by_Fegion_(1).jpg" alt="El Blindaje Operativo AFIP - Pack 5 Tomos + Bonos" className="w-full h-auto" width={1456} height={816} fetchPriority="high" decoding="async" data-id="element-14" />
            </div>

            <p className="text-xl md:text-2xl text-gray-300 font-light max-w-3xl mx-auto leading-relaxed" data-id="element-15">
              Hay contadores que duermen tranquilos después de cada cierre. Y
              hay contadores que esperan con ansiedad.{' '}
              <span className="text-gold-400 font-semibold italic" data-id="element-16">
                La diferencia es un sistema.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* ── PROBLEM ── */}
      <section className="py-20 px-4 bg-navy-900" data-id="element-17">
        <div className="max-w-3xl mx-auto" data-id="element-18">
          <FadeIn data-id="element-19">
            <h2 className="text-3xl md:text-4xl font-bold text-gold-500 font-serif mb-12 text-center" data-id="element-20">
              EL PROBLEMA
            </h2>
          </FadeIn>

          <div className="space-y-6 text-lg md:text-xl text-gray-300 leading-relaxed font-light" data-id="element-21">
            <FadeIn delay={0.1} data-id="element-22">
              <p data-id="element-23">Son las 11 de la noche.</p>
            </FadeIn>
            <FadeIn delay={0.15} data-id="element-24">
              <p data-id="element-25">Tenés el cursor sobre "Presentar".</p>
            </FadeIn>
            <FadeIn delay={0.2} data-id="element-26">
              <p data-id="element-27">Revisaste dos veces. Creés que está todo bien.</p>
            </FadeIn>
            <FadeIn delay={0.25} data-id="element-28">
              <p data-id="element-29">
                Pero algo en tu cabeza dice:{' '}
                <span className="text-gold-400 italic font-serif" data-id="element-30">
                  ¿Y si me equivoqué en algo?
                </span>
              </p>
            </FadeIn>
            <FadeIn delay={0.3} data-id="element-31">
              <p data-id="element-32">Apretás. Y esperás.</p>
            </FadeIn>

            <FadeIn delay={0.1} className="pt-4" data-id="element-33">
              <p data-id="element-34">
                Esa sensación no es normal. No debería ser parte de tu trabajo.
              </p>
              <p data-id="element-35">Pero se convirtió en tu rutina.</p>
            </FadeIn>

            <FadeIn delay={0.1} className="pt-8" data-id="element-36">
              <p className="text-white font-medium mb-6" data-id="element-37">
                Y cuando algo sale mal, sabés exactamente lo que viene:
              </p>
              <ul className="space-y-4 pl-4 border-l-2 border-red-500/50" data-id="element-38">
                {PROBLEM_ITEMS.map((item, idx) => <li key={idx} className="flex items-start" data-id="element-39">
                    <ArrowRight className="w-5 h-5 mr-3 text-red-500 shrink-0 mt-1" data-id="element-40" />{' '}
                    {item}
                  </li>)}
              </ul>
            </FadeIn>

            <FadeIn delay={0.1} className="pt-8 text-center bg-navy-800/50 p-8 rounded-2xl border border-navy-700" data-id="element-41">
              <p className="text-2xl text-white font-serif mb-4" data-id="element-42">
                ¿Cuánto te costó eso el año pasado?
              </p>
              <p className="text-gray-400 mb-6" data-id="element-43">
                En tiempo. En dinero. En clientes. En energía.
              </p>
              <p className="text-xl text-gold-500 font-bold" data-id="element-44">
                Si sos honesto... sabés que demasiado.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── TRUTH ── */}
      <section className="py-20 px-4 bg-navy-800 border-y border-gold-500/10" data-id="element-45">
        <div className="max-w-3xl mx-auto" data-id="element-46">
          <FadeIn data-id="element-47">
            <h2 className="text-3xl md:text-4xl font-bold text-white font-serif mb-12 text-center" data-id="element-48">
              LA VERDAD QUE NADIE TE DICE
            </h2>
          </FadeIn>

          <div className="space-y-6 text-lg text-gray-300" data-id="element-49">
            <FadeIn data-id="element-50">
              <p data-id="element-51">El problema no es que seas mal contador.</p>
              <p className="text-xl text-white font-bold mt-2" data-id="element-52">
                El problema es que trabajás sin un protocolo de prevención.
              </p>
            </FadeIn>

            <FadeIn data-id="element-53">
              <div className="my-10 text-center" data-id="element-54">
                <h3 className="text-2xl md:text-3xl font-bold text-gold-400 font-serif leading-tight" data-id="element-55">
                  El 80% de los errores que generan problemas con ARCA son
                  errores sistémicos y preventibles.
                </h3>
              </div>
            </FadeIn>

            <FadeIn data-id="element-56">
              <div className="bg-navy-900 p-8 rounded-2xl border-l-4 border-gold-500" data-id="element-57">
                <p className="mb-4" data-id="element-58">No son errores técnicos complejos.</p>
                <p className="mb-4" data-id="element-59">
                  Son errores que ocurren porque no existe una revisión
                  estructurada antes de cada presentación.
                </p>
                <p className="text-white font-medium" data-id="element-60">
                  ARCA no te avisa. Te deja presentar. Y después llega el
                  requerimiento.
                </p>
              </div>
            </FadeIn>

            <FadeIn data-id="element-61">
              <div className="text-center space-y-4 text-xl mt-12" data-id="element-62">
                <p className="text-gray-400" data-id="element-63">
                  La solución no es trabajar más horas.
                </p>
                <p className="text-gold-500 font-bold text-2xl mt-4" data-id="element-64">
                  La solución es tener un sistema de blindaje preventivo.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── PRODUCT INTRO ── */}
      <section className="py-20 px-4 bg-navy-900 relative overflow-hidden" data-id="element-65">
        <div className="hidden" aria-hidden="true" data-id="element-66" />
        <div className="max-w-4xl mx-auto text-center relative z-10" data-id="element-67">
          <FadeIn data-id="element-68">
            <div className="flex justify-center mb-6" data-id="element-69">
              <Shield className="w-16 h-16 text-gold-500" aria-hidden="true" data-id="element-70" />
            </div>
            <h2 className="text-4xl md:text-6xl font-bold text-white font-serif mb-4" data-id="element-71">
              EL BLINDAJE OPERATIVO
            </h2>
            <h3 className="text-xl md:text-2xl text-gold-400 font-light mb-2" data-id="element-72">
              El Protocolo de Auditoría Preventiva Anti-SIAp
            </h3>
            <p className="text-lg text-gray-400 italic mb-12" data-id="element-73">
              Pack 5 Tomos + 3 Bonos — by Fegion
            </p>
          </FadeIn>

          <FadeIn delay={0.15} data-id="element-74">
            <div className="max-w-2xl mx-auto text-center space-y-4 text-lg text-gray-300 bg-navy-800/50 p-8 rounded-2xl border border-gold-500/20" data-id="element-75">
              <p data-id="element-76">No es un curso. No es teoría. No es normativa resumida.</p>
              <p className="text-xl font-bold text-gold-500" data-id="element-77">
                Es tu protocolo operativo. Tu sistema. Tu escudo.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── 5 TOMOS ── */}
      <section className="py-16 px-4 bg-navy-800" data-id="element-78">
        <div className="max-w-4xl mx-auto" data-id="element-79">
          <FadeIn data-id="element-80">
            <div className="text-center mb-12" data-id="element-81">
              <BookOpen className="w-10 h-10 text-gold-500 mx-auto mb-4" aria-hidden="true" data-id="element-82" />
              <h2 className="text-3xl font-bold text-white font-serif mb-4" data-id="element-83">
                LOS 5 TOMOS
              </h2>
            </div>
          </FadeIn>

          <div className="space-y-6" data-id="element-84">
            {TOMOS.map((tomo, idx) => <FadeIn key={tomo.num} delay={idx * 0.08} data-id="element-85">
                <div className="bg-navy-900 border-l-4 border-gold-500 rounded-r-xl p-6 shadow-md" data-id="element-86">
                  <h3 className="text-xl font-bold text-white font-serif mb-2" data-id="element-87">
                    <span className="text-gold-500 mr-2" data-id="element-88">
                      📘 TOMO {tomo.num} —
                    </span>{' '}
                    {tomo.title}
                  </h3>
                  <p className="text-gray-300" data-id="element-89">{tomo.desc}</p>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* ── 3 BONOS ── */}
      <section className="py-16 px-4 bg-navy-900 border-t border-gold-500/10" data-id="element-90">
        <div className="max-w-4xl mx-auto" data-id="element-91">
          <FadeIn data-id="element-92">
            <div className="text-center mb-12" data-id="element-93">
              <Gift className="w-10 h-10 text-gold-500 mx-auto mb-4" aria-hidden="true" data-id="element-94" />
              <h2 className="text-3xl font-bold text-white font-serif mb-4" data-id="element-95">
                LOS 3 BONOS INCLUIDOS
              </h2>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-6" data-id="element-96">
            <FadeIn delay={0.08} className="h-full" data-id="element-97">
              <div className="bg-navy-800 border-t-4 border-green-500 rounded-b-xl p-6 h-full shadow-lg" data-id="element-98">
                <h3 className="text-lg font-bold text-white font-serif mb-3" data-id="element-99">
                  🟢 BONO 1<br data-id="element-100" />
                  <span className="text-green-400" data-id="element-101">DFE en 3 Minutos</span>
                </h3>
                <p className="text-sm text-gray-300" data-id="element-102">
                  El protocolo semanal para revisar tu Domicilio Fiscal
                  Electrónico y nunca más perderte una notificación de ARCA.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.16} className="h-full" data-id="element-103">
              <div className="bg-navy-800 border-t-4 border-blue-500 rounded-b-xl p-6 h-full shadow-lg" data-id="element-104">
                <h3 className="text-lg font-bold text-white font-serif mb-3" data-id="element-105">
                  🔵 BONO 2<br data-id="element-106" />
                  <span className="text-blue-400" data-id="element-107">Checklist Anti-Rechazo</span>
                </h3>
                <p className="text-sm text-gray-300" data-id="element-108">
                  27 controles exactos antes de apretar "Presentar". Cero
                  rechazos evitables.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.24} className="h-full" data-id="element-109">
              <div className="bg-navy-800 border-t-4 border-amber-500 rounded-b-xl p-6 h-full shadow-lg" data-id="element-110">
                <h3 className="text-lg font-bold text-white font-serif mb-3" data-id="element-111">
                  🟤 BONO 3<br data-id="element-112" />
                  <span className="text-amber-400" data-id="element-113">
                    Mapa Express de Servicios
                  </span>
                </h3>
                <p className="text-sm text-gray-300" data-id="element-114">
                  Qué cobrar, cuándo y cómo. Para dejar de regalar trabajo esta
                  semana.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── COMPARISON TABLE ── */}
      <section className="py-16 px-4 bg-navy-800" data-id="element-115">
        <div className="max-w-3xl mx-auto" data-id="element-116">
          <FadeIn data-id="element-117">
            <h2 className="text-3xl font-bold text-center text-white font-serif mb-10" data-id="element-118">
              LO QUE CAMBIA EN TU TRABAJO
            </h2>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-119">
            <div className="rounded-2xl border border-gold-500/30 shadow-xl overflow-hidden" data-id="element-120">
              <table className="w-full text-left border-collapse" data-id="element-121">
                <thead data-id="element-122">
                  <tr className="bg-navy-900 border-b border-gold-500/30" data-id="element-123">
                    <th className="p-3 md:p-5 text-red-400 font-bold text-center w-1/2 text-xs md:text-base" data-id="element-124">
                      ❌ Sin el Blindaje
                    </th>
                    <th className="p-3 md:p-5 text-green-400 font-bold text-center w-1/2 border-l border-navy-700 bg-gold-500/5 text-xs md:text-base" data-id="element-125">
                      ✅ Con el Blindaje
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy-700 bg-navy-800 text-xs md:text-base" data-id="element-126">
                  {COMPARISON_ROWS.map((row, idx) => <tr key={idx} data-id="element-127">
                      <td className="p-3 md:p-4 text-gray-400 text-center" data-id="element-128">
                        {row.bad}
                      </td>
                      <td className="p-3 md:p-4 text-gold-300 font-medium border-l border-navy-700 text-center bg-gold-500/5" data-id="element-129">
                        {row.good}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className="py-20 px-4 bg-navy-900 border-t border-gold-500/10" data-id="element-130">
        <div className="max-w-4xl mx-auto" data-id="element-131">
          <FadeIn data-id="element-132">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white font-serif mb-16" data-id="element-133">
              LO QUE DICEN LOS CONTADORES QUE YA LO TIENEN
            </h2>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-134">
            {TESTIMONIALS.map((t, idx) => <FadeIn key={idx} delay={idx * 0.06} data-id="element-135">
                <div className="bg-navy-800 p-6 rounded-2xl border border-navy-700 h-full flex flex-col" data-id="element-136">
                  <div className="flex text-gold-500 mb-3" aria-label={`${t.stars} de 5 estrellas`} data-id="element-137">
                    {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} filled={i < t.stars} data-id="element-138" />)}
                  </div>
                  <h4 className="text-white font-bold mb-3 text-sm" data-id="element-139">
                    {t.title}
                  </h4>
                  <p className="text-gray-400 text-sm italic leading-relaxed flex-1" data-id="element-140">
                    "{t.quote}"
                  </p>
                  <div className="mt-5 pt-4 border-t border-navy-700 flex items-center gap-3" data-id="element-141">
                    <img src={t.img} alt={t.name} className="w-10 h-10 rounded-full object-cover border-2 border-navy-600" width={40} height={40} loading="lazy" decoding="async" data-id="element-142" />
                    <div data-id="element-143">
                      <p className="text-white font-bold text-sm" data-id="element-144">{t.name}</p>
                      <p className="text-gray-500 text-xs" data-id="element-145">
                        {t.role} · {t.location}
                      </p>
                    </div>
                  </div>
                </div>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* ── UPSELL INTRO ── */}
      <section className="py-20 px-4 bg-navy-900 border-t border-gold-500/20 relative overflow-hidden" data-id="element-146">
        <div className="hidden" aria-hidden="true" data-id="element-147" />
        <div className="max-w-3xl mx-auto relative z-10" data-id="element-148">
          <FadeIn data-id="element-149">
            <div className="text-center mb-12" data-id="element-150">
              <span className="text-4xl mb-4 block" aria-hidden="true" data-id="element-151">
                ⚡
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-white font-serif mb-3" data-id="element-152">
                MOMENTO — ANTES DE CONFIRMAR TU COMPRA
              </h2>
              <p className="text-xl text-gold-400 italic" data-id="element-153">
                Agregá esto a tu pack y completá el sistema en un solo paso
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-154">
            <div className="text-center mb-12" data-id="element-155">
              <p className="text-lg text-gray-300 mb-2" data-id="element-156">
                El Blindaje te protege de ARCA.
              </p>
              <h3 className="text-3xl md:text-4xl font-bold text-white font-serif" data-id="element-157">
                Pero, ¿quién protege tu rentabilidad?
              </h3>
            </div>
          </FadeIn>

          <FadeIn delay={0.15} data-id="element-158">
            <div className="space-y-5 text-lg text-gray-300 mb-12" data-id="element-159">
              <p data-id="element-160">Trabajás más que nunca.</p>
              <p data-id="element-161">Tenés más clientes que antes.</p>
              <p className="text-white font-medium" data-id="element-162">
                Y sin embargo... los números a fin de mes no reflejan todo ese
                esfuerzo.
              </p>
              <p className="mt-6" data-id="element-163">¿Por qué?</p>
              <p data-id="element-164">
                Porque{' '}
                <strong className="text-white" data-id="element-165">blindar la operación</strong> y{' '}
                <strong className="text-white" data-id="element-166">
                  gestionar la rentabilidad
                </strong>{' '}
                son dos cosas distintas.
              </p>
              <p data-id="element-167">Y la mayoría de los contadores solo trabajan en una.</p>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-168">
            <div className="text-center mb-10" data-id="element-169">
              <p className="text-gold-500 font-bold tracking-widest uppercase text-sm mb-4" data-id="element-170">
                🏆 MEJORÁ TU PACK CON:
              </p>
              <h2 className="text-5xl md:text-6xl font-bold text-white font-serif mb-3" data-id="element-171">
                ESTUDIO 10X
              </h2>
              <h3 className="text-xl md:text-2xl text-gold-400 mb-1" data-id="element-172">
                Master de Gestión, Honorarios y Rentabilidad
              </h3>
              <p className="text-gray-400 italic" data-id="element-173">
                Para el Contador Público Argentino
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.25} data-id="element-174">
            <div className="bg-navy-800/60 border border-navy-600 rounded-2xl p-8 space-y-5" data-id="element-175">
              {ESTUDIO_FEATURES.map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-176">
                  <Check className="w-6 h-6 text-green-500 shrink-0 mt-0.5" aria-hidden="true" data-id="element-177" />
                  <p className="text-gray-200" data-id="element-178">
                    <strong className="text-white" data-id="element-179">{item.title}</strong> —{' '}
                    {item.desc}
                  </p>
                </div>)}
            </div>
          </FadeIn>

          {/* IA AUTOMATION BONUS */}
          <FadeIn delay={0.3} data-id="element-180">
            <div className="mt-12 bg-gradient-to-br from-navy-800 to-navy-900 border border-gold-500/30 rounded-2xl p-8 md:p-10 relative overflow-hidden" data-id="element-181">
              <div className="hidden" aria-hidden="true" data-id="element-182" />
              <div className="relative z-10" data-id="element-183">
                <div className="flex items-center gap-3 mb-6" data-id="element-184">
                  <Zap className="w-8 h-8 text-gold-500 fill-gold-500" aria-hidden="true" data-id="element-185" />
                  <div data-id="element-186">
                    <h3 className="text-xl md:text-2xl font-bold text-white font-serif" data-id="element-187">
                      + SISTEMA DE AUTOMATIZACIÓN CON IA
                    </h3>
                    <span className="text-gold-400 text-sm font-bold uppercase tracking-wider" data-id="element-188">
                      (BONO INCLUIDO)
                    </span>
                  </div>
                </div>

                <p className="text-lg text-gray-300 mb-2" data-id="element-189">
                  Ahorrá hasta{' '}
                  <strong className="text-white" data-id="element-190">10 horas por semana</strong>{' '}
                  usando inteligencia artificial en tu estudio contable.
                </p>
                <p className="text-gray-400 mb-6" data-id="element-191">
                  Incluye{' '}
                  <strong className="text-white" data-id="element-192">
                    100 prompts listos para usar en ChatGPT
                  </strong>
                  , diseñados para contadores en Argentina.
                </p>

                <div className="space-y-3 mb-8" data-id="element-193">
                  {IA_FEATURES.map((item, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-194">
                      <Check className="w-5 h-5 text-green-500 shrink-0 mt-0.5" aria-hidden="true" data-id="element-195" />
                      <p className="text-gray-200" data-id="element-196">{item}</p>
                    </div>)}
                </div>

                <div className="bg-navy-900/80 border border-navy-600 rounded-xl p-5 mb-6" data-id="element-197">
                  <p className="text-gray-400 text-sm font-medium mb-2" data-id="element-198">
                    Ejemplo real:
                  </p>
                  <p className="text-gold-300 italic text-sm leading-relaxed" data-id="element-199">
                    "Actuá como un contador argentino. Explicá en términos
                    simples qué es el Monotributo, cómo funciona y cuáles son
                    sus obligaciones."
                  </p>
                </div>

                <p className="text-center text-lg" data-id="element-200">
                  <span className="text-gray-400" data-id="element-201">
                    👉 Menos tiempo operativo.
                  </span>{' '}
                  <span className="text-gold-400 font-bold" data-id="element-202">
                    Más foco en lo que realmente te genera ingresos.
                  </span>
                </p>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── OFFER CARDS ── */}
      <section className="py-20 px-4 bg-navy-900 relative" data-id="element-203">
        <div className="absolute inset-0 bg-navy-900 -z-10" aria-hidden="true" data-id="element-204" />
        <div className="max-w-4xl mx-auto" data-id="element-205">
          <FadeIn data-id="element-206">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-white font-serif mb-4" data-id="element-207">
              ELEGÍ TU OPCIÓN
            </h2>
            <p className="text-center text-gray-400 mb-12" data-id="element-208">
              Seleccioná el pack que mejor se adapte a tu estudio.
            </p>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-209">
            <div className="max-w-md mx-auto bg-[#111827] p-4 md:p-6 rounded-[2rem] border border-navy-600 shadow-2xl" data-id="element-210">
              {/* Pro Card */}
              <div onClick={() => setSelectedOffer('pro')} role="radio" aria-checked={selectedOffer === 'pro'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && setSelectedOffer('pro')} className={`relative p-5 rounded-2xl border-2 cursor-pointer transition-all mb-4 flex justify-between items-start ${selectedOffer === 'pro' ? 'border-green-500 bg-navy-800/80 shadow-[0_0_15px_rgba(34,197,94,0.15)]' : 'border-navy-600 bg-navy-800/40 hover:border-navy-500'}`} data-id="element-211">
                <div className="flex items-start gap-4" data-id="element-212">
                  <div className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${selectedOffer === 'pro' ? 'border-green-500' : 'border-gray-500'}`} data-id="element-213">
                    {selectedOffer === 'pro' && <div className="w-3 h-3 bg-green-500 rounded-full" data-id="element-214" />}
                  </div>
                  <div data-id="element-215">
                    <div className="flex items-center gap-2 mb-2" data-id="element-216">
                      <Trophy className="w-5 h-5 text-gold-500 fill-gold-500" aria-hidden="true" data-id="element-217" />
                      <span className="font-bold text-white text-lg tracking-wide" data-id="element-218">
                        COMBO PRO
                      </span>
                    </div>
                    <div className="flex flex-col gap-2 mb-3 items-start" data-id="element-219">
                      <span className="bg-green-600 text-white text-[10px] sm:text-xs font-bold px-2 py-1 rounded flex items-center gap-1 uppercase tracking-wider" data-id="element-220">
                        <Star className="w-3 h-3 fill-white" aria-hidden="true" data-id="element-221" />{' '}
                        MÁS ELEGIDO
                      </span>
                      <span className="border border-green-500/50 text-green-400 text-[10px] sm:text-xs font-bold px-2 py-1 rounded-full uppercase tracking-wider" data-id="element-222">
                        AHORRAS $10.000
                      </span>
                    </div>
                    <ul className="text-gray-400 text-sm space-y-1.5" data-id="element-223">
                      <li data-id="element-224">5 tomos + 3 bonos</li>
                      <li className="text-white font-medium" data-id="element-225">+ Estudio 10X</li>
                      <li className="text-gray-500 text-xs pl-2" data-id="element-226">
                        - Sistema de Honorarios
                      </li>
                      <li className="text-gray-500 text-xs pl-2" data-id="element-227">
                        - Gestión y Rentabilidad
                      </li>
                      <li className="text-white font-medium" data-id="element-228">
                        + Sistema de Automatización con IA
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="text-right" data-id="element-229">
                  <p className="text-gray-500 line-through text-sm font-medium" data-id="element-230">
                    $69.980
                  </p>
                  <p className="text-3xl font-bold text-green-500" data-id="element-231">$29.990</p>
                </div>
              </div>

              {/* Base Card */}
              <div onClick={() => setSelectedOffer('base')} role="radio" aria-checked={selectedOffer === 'base'} tabIndex={0} onKeyDown={e => e.key === 'Enter' && setSelectedOffer('base')} className={`relative p-5 rounded-2xl border-2 cursor-pointer transition-all mb-6 flex justify-between items-center ${selectedOffer === 'base' ? 'border-green-500 bg-navy-800/80 shadow-[0_0_15px_rgba(34,197,94,0.15)]' : 'border-navy-600 bg-navy-800/40 hover:border-navy-500'}`} data-id="element-232">
                <div className="flex items-start gap-4" data-id="element-233">
                  <div className={`mt-1 w-6 h-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${selectedOffer === 'base' ? 'border-green-500' : 'border-gray-500'}`} data-id="element-234">
                    {selectedOffer === 'base' && <div className="w-3 h-3 bg-green-500 rounded-full" data-id="element-235" />}
                  </div>
                  <div data-id="element-236">
                    <div className="flex items-center gap-2 mb-1" data-id="element-237">
                      <Heart className="w-5 h-5 text-green-500 fill-green-500" aria-hidden="true" data-id="element-238" />
                      <span className="font-bold text-white text-lg tracking-wide" data-id="element-239">
                        PACK BASE
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm" data-id="element-240">5 tomos + 3 bonos</p>
                  </div>
                </div>
                <div className="text-right" data-id="element-241">
                  <p className="text-gray-500 line-through text-sm font-medium" data-id="element-242">
                    $49.980
                  </p>
                  <p className="text-2xl font-bold text-white" data-id="element-243">$19.990</p>
                </div>
              </div>

              {/* CTA */}
              <a href={ctaUrl} target="_blank" rel="noopener noreferrer" className="w-full bg-[#2E7D32] hover:bg-[#1B5E20] text-white font-bold text-lg py-4 px-6 rounded-xl shadow-[0_0_20px_rgba(46,125,50,0.4)] transition-colors duration-200 flex items-center justify-center gap-2 uppercase tracking-wide" data-id="element-244">
                <Heart className="w-5 h-5 fill-white" aria-hidden="true" data-id="element-245" />
                EMPEZAR HOY — ${selectedOffer === 'base' ? '19.990' : '29.990'}
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── PACK COMPARISON TABLE ── */}
      <section className="py-16 px-4 bg-navy-800 border-t border-gold-500/10" data-id="element-246">
        <div className="max-w-3xl mx-auto" data-id="element-247">
          <FadeIn data-id="element-248">
            <div className="rounded-2xl border border-gold-500/30 shadow-xl overflow-hidden" data-id="element-249">
              <table className="w-full text-left border-collapse" data-id="element-250">
                <thead data-id="element-251">
                  <tr className="bg-navy-900 border-b border-gold-500/30" data-id="element-252">
                    <th className="p-2 md:p-5 text-gray-400 font-medium text-xs md:text-base w-[40%]" data-id="element-253"></th>
                    <th className="p-2 md:p-5 text-center border-l border-navy-700 text-xs md:text-base w-[30%]" data-id="element-254">
                      <span className="text-white font-bold" data-id="element-255">
                        <span className="hidden md:inline" data-id="element-256">📦 </span>PACK BASE
                      </span>
                    </th>
                    <th className="p-2 md:p-5 text-center border-l border-navy-700 bg-gold-500/5 text-xs md:text-base w-[30%]" data-id="element-257">
                      <span className="text-gold-400 font-bold" data-id="element-258">
                        <span className="hidden md:inline" data-id="element-259">🏆 </span>BASE + 10X
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-navy-700 bg-navy-800 text-xs md:text-base" data-id="element-260">
                  {PACK_ROWS.map((row, idx) => <tr key={idx} data-id="element-261">
                      <td className="p-2 md:p-4 text-gray-300 font-medium" data-id="element-262">
                        {row.label}
                      </td>
                      <td className="p-2 md:p-4 text-center border-l border-navy-700" data-id="element-263">
                        <span className={row.base ? 'text-green-500' : 'text-red-500'} data-id="element-264">
                          {row.base ? '✅' : '❌'}
                        </span>
                      </td>
                      <td className="p-2 md:p-4 text-center border-l border-navy-700 bg-gold-500/5" data-id="element-265">
                        <span className={row.pro ? 'text-green-500' : 'text-red-500'} data-id="element-266">
                          {row.pro ? '✅' : '❌'}
                        </span>
                      </td>
                    </tr>)}
                  <tr className="bg-navy-900/50 border-t-2 border-gold-500/30" data-id="element-267">
                    <td className="p-2 md:p-4 text-white font-bold text-xs md:text-base" data-id="element-268">
                      PRECIO
                    </td>
                    <td className="p-2 md:p-4 text-center border-l border-navy-700 text-white font-bold text-sm md:text-lg" data-id="element-269">
                      $19.990
                    </td>
                    <td className="p-2 md:p-4 text-center border-l border-navy-700 bg-gold-500/5 text-gold-400 font-bold text-sm md:text-lg" data-id="element-270">
                      $29.990
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-271">
            <div className="mt-10 text-center bg-navy-900/50 p-6 rounded-2xl border border-gold-500/20" data-id="element-272">
              <p className="text-gold-400 font-bold text-xl mb-2" data-id="element-273">
                💡 Solo $10.000 ARS más.
              </p>
              <p className="text-gray-300" data-id="element-274">
                El Estudio 10X por separado vale $19.990 ARS.
              </p>
              <p className="text-gold-400 italic text-sm mt-1" data-id="element-275">
                Agregándolo ahora lo llevás a la mitad del precio.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── DUAL CTA ── */}
      <section className="py-20 px-4 bg-navy-900 relative" data-id="element-276">
        <div className="absolute inset-0 bg-navy-900 -z-10" aria-hidden="true" data-id="element-277" />
        <div className="max-w-2xl mx-auto text-center" data-id="element-278">
          <FadeIn data-id="element-279">
            <p className="text-red-500 font-bold tracking-widest uppercase text-sm mb-8" data-id="element-280">
              🔴 ELEGÍ TU OPCIÓN:
            </p>

            {/* Option A */}
            <div className="bg-navy-800 border-2 border-green-500 rounded-2xl p-8 md:p-10 mb-6 shadow-[0_0_30px_rgba(34,197,94,0.1)]" data-id="element-281">
              <p className="text-green-400 font-bold uppercase tracking-wider text-sm mb-2" data-id="element-282">
                OPCIÓN A — PACK COMPLETO RECOMENDADO
              </p>
              <h3 className="text-2xl font-bold text-white font-serif mb-4" data-id="element-283">
                Blindaje Operativo+ Estudio 10X + IA
              </h3>
              <div className="flex items-center justify-center gap-3 mb-6" data-id="element-284">
                <span className="text-gray-500 line-through text-xl" data-id="element-285">
                  $69.980
                </span>
                <ArrowRight className="w-5 h-5 text-gold-500" aria-hidden="true" data-id="element-286" />
                <span className="text-3xl font-black text-white font-serif" data-id="element-287">
                  $29.990{' '}
                  <span className="text-base text-gray-400 font-sans font-normal" data-id="element-288">
                    ARS
                  </span>
                </span>
              </div>
              <a href="https://fegion.shop/cart/46187434344587:1" target="_blank" rel="noopener noreferrer" className="w-full block bg-[#2E7D32] hover:bg-[#1B5E20] text-white font-bold text-lg py-5 px-8 rounded-xl shadow-[0_0_25px_rgba(46,125,50,0.4)] transition-colors duration-200 uppercase tracking-wide mb-4 text-center" data-id="element-289">
                → SÍ, QUIERO EL COMBO COMPLETO ←
              </a>
              <p className="text-gray-400 text-sm" data-id="element-290">
                ✅ Todo incluido · ✅ Acceso inmediato · ✅ Garantía 7 días
              </p>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4 my-8" aria-hidden="true" data-id="element-291">
              <div className="flex-1 h-px bg-navy-700" data-id="element-292" />
              <span className="text-gray-500 font-medium" data-id="element-293">— o —</span>
              <div className="flex-1 h-px bg-navy-700" data-id="element-294" />
            </div>

            {/* Option B */}
            <div className="mb-8" data-id="element-295">
              <p className="text-gray-400 uppercase tracking-wider text-sm mb-2" data-id="element-296">
                OPCIÓN B — Solo el Pack Base
              </p>
              <p className="text-xl text-white font-bold mb-4" data-id="element-297">$19.990 ARS</p>
              <a href="https://fegion.shop/cart/46187419336843:1" target="_blank" rel="noopener noreferrer" className="w-full block bg-[#D4874D] hover:bg-[#C07843] text-white font-bold text-base py-4 px-8 rounded-xl shadow-[0_0_15px_rgba(212,135,77,0.25)] transition-colors duration-200 mb-2 text-center" data-id="element-298">
                No gracias, prefiero solo el Blindaje Operativo ARCA(Ex-AFIP)
              </a>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── GUARANTEE ── */}
      <section className="py-16 px-4 bg-navy-800 border-t border-gold-500/10" data-id="element-299">
        <div className="max-w-2xl mx-auto" data-id="element-300">
          <FadeIn data-id="element-301">
            <div className="bg-navy-900 border border-gold-500/30 rounded-2xl p-8 text-center shadow-lg" data-id="element-302">
              <Lock className="w-12 h-12 text-gold-500 mx-auto mb-4" aria-hidden="true" data-id="element-303" />
              <h2 className="text-2xl font-bold text-white font-serif mb-4" data-id="element-304">
                🔒 GARANTÍA EN AMBAS OPCIONES
              </h2>
              <p className="text-gray-300 text-lg" data-id="element-305">
                7 días de garantía total. Devolución del 100% sin preguntas.
              </p>
              <p className="text-gold-400 font-medium mt-4" data-id="element-306">
                Sea cual sea la opción que elijas — estás completamente
                protegido.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="py-10 px-4 bg-navy-900 border-t border-navy-700 text-center" data-id="element-307">
        <Shield className="w-6 h-6 text-gold-500/50 mx-auto mb-3" aria-hidden="true" data-id="element-308" />
        <p className="text-gold-500/70 font-serif italic text-sm" data-id="element-309">
          by Fegion — Para el Contador Público Argentino que trabaja en serio.
        </p>
      </footer>
    </main>;
}